/**
 * Helper to add test data to Firestore via REST API only
 * Web platform uses HTTP REST API instead of SDK
 * Mobile continues to use SDK
 */

import {
  writeDocumentViaREST,
  readDocumentViaREST,
  readCollectionViaREST,
  deleteDocumentViaREST,
} from './firebaseRestApi';
import { ChatType, ConciergeChatType, BUTTON_STATUS_ENUM } from '~types/chat';
import moment from 'moment';


/**
 * Verify that a written document has synced to backend by reading it via REST API
 * This ensures the write is confirmed on the server
 */
async function verifyWriteToBackend(collectionName: string, docId: string): Promise<boolean> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.error(`❌ [TEST-DATA] Verification timeout - REST API read did not complete in 5 seconds`);
      resolve(false);
    }, 5000);

    (async () => {
      try {
        console.log(`⏳ [TEST-DATA] Verifying write to backend for: ${collectionName}/${docId}`);
        
        // Try to read the document
        console.log(`📖 [TEST-DATA] Reading document from Firestore via REST API...`);
        const docData = await readDocumentViaREST(collectionName, docId);
        
        clearTimeout(timeout);
        
        if (docData) {
          console.log(`✅ [TEST-DATA] WRITE VERIFIED! Data successfully on backend: ${collectionName}/${docId}`);
          console.log(`✅ [TEST-DATA] Document data:`, docData);
          resolve(true);
        } else {
          console.error(`❌ [TEST-DATA] Document not found on backend: ${collectionName}/${docId}`);
          resolve(false);
        }
      } catch (error) {
        clearTimeout(timeout);
        console.error(`❌ [TEST-DATA] Error verifying write:`, error);
        resolve(false);
      }
    })();
  });
}

export async function addTestChats(userId?: string) {
  try {
    const CURRENT_USER_ID = userId || 'a92186f6-7d31-4661-8a44-8c2ef5d57cd5';
    console.log('📝 [TEST-DATA] Adding test chats for user:', CURRENT_USER_ID);
    console.log('📝 [TEST-DATA] Using REST API (no SDK) for all operations');
    
    // Check if test data already exists
    console.log('📝 [TEST-DATA] Checking for existing chats...');
    const existingChats = await readCollectionViaREST(
      'chats',
      [{ field: 'userIds', operator: 'array-contains', value: CURRENT_USER_ID }]
    );
    
    console.log('📝 [TEST-DATA] Existing chats found:', existingChats.length);
    
    if (existingChats.length > 0) {
      console.log('ℹ️ [TEST-DATA] Test chats already exist for this user. Count:', existingChats.length);
      existingChats.forEach((chat: any) => {
        console.log('📝 [TEST-DATA] Existing chat:', chat.id);
      });
      return {
        success: false,
        message: 'Test data already exists',
        chats: existingChats.map((c: any) => c.id),
      };
    }

    // Test chat 1: Chat with another user
    const testChat1: ChatType = {
      history: [
        {
          message_id: '1',
          message: 'Hey! How are you?',
          sender: 'John Doe',
          sender_image: 'https://via.placeholder.com/48',
          sender_id: 'user-456',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          type: 'message' as any,
          readUserIds: [CURRENT_USER_ID],
        },
        {
          message_id: '2',
          message: 'I\'m doing great! How about you?',
          sender: 'You',
          sender_image: 'https://via.placeholder.com/48',
          sender_id: CURRENT_USER_ID,
          timestamp: new Date(Date.now() - 1800000).toISOString(),
          type: 'message' as any,
          readUserIds: ['user-456'],
        },
        {
          message_id: '3',
          message: 'Great! Let\'s meet up soon',
          sender: 'John Doe',
          sender_image: 'https://via.placeholder.com/48',
          sender_id: 'user-456',
          timestamp: new Date().toISOString(),
          type: 'message' as any,
          readUserIds: [],
        },
      ],
      type: 'private',
      userIds: [CURRENT_USER_ID, 'user-456'],
      users: ['You', 'John Doe'],
      status: 'active',
      progress: '0',
      blockedUsers: {},
      blockedBy: {},
    };

    console.log('📝 [TEST-DATA] Creating test chat 1 via REST API...');
    const doc1 = await writeDocumentViaREST('chats', {
      ...testChat1,
      lastMessage: testChat1.history[testChat1.history.length - 1]?.message,
      lastMessageTime: moment().unix(),
      createdAt: moment().unix(),
    });
    console.log('✅ [TEST-DATA] Chat 1 created:', doc1.id);
    
    // Fire verification async
    verifyWriteToBackend('chats', doc1.id).then(synced => {
      if (synced) {
        console.log('✅ [TEST-DATA] VERIFIED: Chat 1 synced to backend!');
      } else {
        console.error('❌ [TEST-DATA] FAILED: Chat 1 not found on backend');
      }
    }).catch(err => {
      console.error('❌ [TEST-DATA] Verification error:', err);
    });

    // Test chat 2: Chat with another user
    const testChat2: ChatType = {
      history: [
        {
          message_id: '1',
          message: 'Hey, how\'s the project going?',
          sender: 'Sarah Smith',
          sender_image: 'https://via.placeholder.com/48',
          sender_id: 'user-789',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          type: 'message' as any,
          readUserIds: [CURRENT_USER_ID],
        },
        {
          message_id: '2',
          message: 'Almost done! Should be finished by tomorrow',
          sender: 'You',
          sender_image: 'https://via.placeholder.com/48',
          sender_id: CURRENT_USER_ID,
          timestamp: new Date(Date.now() - 82800000).toISOString(),
          type: 'message' as any,
          readUserIds: ['user-789'],
        },
      ],
      type: 'private',
      userIds: [CURRENT_USER_ID, 'user-789'],
      users: ['You', 'Sarah Smith'],
      status: 'active',
      progress: '0',
      blockedUsers: {},
      blockedBy: {},
    };

    console.log('📝 [TEST-DATA] Creating test chat 2 via REST API...');
    const doc2 = await writeDocumentViaREST('chats', {
      ...testChat2,
      lastMessage: testChat2.history[testChat2.history.length - 1]?.message,
      lastMessageTime: moment().unix(),
      createdAt: moment().unix(),
    });
    console.log('✅ [TEST-DATA] Chat 2 created:', doc2.id);
    
    // Fire verification async
    verifyWriteToBackend('chats', doc2.id).then(synced => {
      if (synced) {
        console.log('✅ [TEST-DATA] VERIFIED: Chat 2 synced to backend!');
      } else {
        console.error('❌ [TEST-DATA] FAILED: Chat 2 not found on backend');
      }
    }).catch(err => {
      console.error('❌ [TEST-DATA] Verification error:', err);
    });

    // Test concierge chat
    const testConciergeChat: ConciergeChatType = {
      type: 'one-on-one',
      userId: CURRENT_USER_ID,
      userName: 'You',
      messages: [
        {
          status: BUTTON_STATUS_ENUM.SENT,
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          senderId: 'concierge-bot',
          sender: 'Pyxi Concierge',
          message: 'Welcome to Pyxi! How can I help you today?',
          readUserIds: [CURRENT_USER_ID],
        },
        {
          status: BUTTON_STATUS_ENUM.SENT,
          timestamp: new Date().toISOString(),
          senderId: CURRENT_USER_ID,
          sender: 'You',
          message: 'Hi! I have a question about events',
          readUserIds: ['concierge-bot'],
        },
      ],
    };

    console.log('📝 [TEST-DATA] Creating concierge chat via REST API...');
    const doc3 = await writeDocumentViaREST('conciergeChat', {
      ...testConciergeChat,
      lastMessageTime: moment().unix(),
      createdAt: moment().unix(),
    });
    console.log('✅ [TEST-DATA] Concierge chat created:', doc3.id);

    console.log('✅ [TEST-DATA] All test data added successfully! Created 2 chats + 1 concierge chat');
    
    // Verify data was written
    console.log('📝 [TEST-DATA] Verifying data was written to Firestore...');
    try {
      const allChats = await readCollectionViaREST('chats', undefined);
      console.log('✅ [TEST-DATA] Verification: Total chats in collection after insert:', allChats.length);
      
      allChats.forEach((chat: any, idx: number) => {
        console.log(`📄 [TEST-DATA] Chat ${idx + 1}: ${chat.id}`, {
          userIds: chat.userIds,
          users: chat.users,
          type: chat.type,
          historyLength: chat.history?.length || 0,
        });
      });
    } catch (verifyError) {
      console.error('❌ [TEST-DATA] Error verifying data:', verifyError);
    }
    
    console.log('✅ [TEST-DATA] Final result: All data written via HTTP REST API');
    
    return {
      success: true,
      chats: [doc1.id, doc2.id],
      concierge: doc3.id,
    };
  } catch (error) {
    console.error('❌ [TEST-DATA] Error adding test data:', error);
    console.error('❌ [TEST-DATA] Error details:', {
      message: (error as any).message,
      code: (error as any).code,
      name: (error as any).name,
    });
    throw error;
  }
}

/**
 * Delete all test data for a user
 * Uses REST API only
 */
export async function deleteTestChats(userId?: string) {
  try {
    const CURRENT_USER_ID = userId || 'a92186f6-7d31-4661-8a44-8c2ef5d57cd5';
    console.log('🗑️ [TEST-DATA] Deleting test chats for user:', CURRENT_USER_ID);
    
    const existingChats = await readCollectionViaREST(
      'chats',
      [{ field: 'userIds', operator: 'array-contains', value: CURRENT_USER_ID }]
    );
    
    console.log('🗑️ [TEST-DATA] Found', existingChats.length, 'chats to delete');
    
    // Delete each chat
    for (const chatDoc of existingChats) {
      try {
        await deleteDocumentViaREST('chats', chatDoc.id);
        console.log('✅ [TEST-DATA] Deleted:', chatDoc.id);
      } catch (err) {
        console.error('❌ [TEST-DATA] Error deleting chat', chatDoc.id, ':', err);
      }
    }
    
    console.log('✅ [TEST-DATA] Delete complete - polling listeners will refresh automatically');
    return {
      success: true,
      deleted: existingChats.length,
      chatIds: existingChats.map((c: any) => c.id),
    };
  } catch (error) {
    console.error('❌ [TEST-DATA] Error deleting test data:', error);
    throw error;
  }
}

/**
 * Call this from your component or console to populate test data
 * Example: window.addTestChats?.()
 */
if (typeof window !== 'undefined') {
  (window as any).addTestChats = addTestChats;
  (window as any).deleteTestChats = deleteTestChats;
}
